import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import useOrganization from "../../organization/hooks/useOrganization";
import { useProfile } from "../../auth/hooks/useProfile";
import { useCoach } from "../../coach/contexts/CoachProvider";
import { getTeamSurvey } from "../api/team.api";
import type { TeamSurveyData } from "../types/teamSurvey.types";

export type TeamSelection = { id: number; name: string } | null;

export type TeamContextValue = {
  selectedDepartment: { id: number; name: string } | null;
  setSelectedDepartment: React.Dispatch<
    React.SetStateAction<{ id: number; name: string } | null>
  >;
  selectedTeam: TeamSelection;
  setSelectedTeam: React.Dispatch<React.SetStateAction<TeamSelection>>;
  teamSurveyData: TeamSurveyData | null;
  teamSurveyLoading: boolean;
  teamSurveyError: string | null;
  hasTeamCompletionProgress: boolean;
  refetchTeamSurvey: () => Promise<void>;
};

const TeamContext = createContext<TeamContextValue | null>(null);

export const TeamProvider = ({ children }: { children: React.ReactNode }) => {
  const [selectedDepartment, setSelectedDepartment] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const [selectedTeam, setSelectedTeam] = useState<TeamSelection>(null);

  const [teamSurveyData, setTeamSurveyData] = useState<TeamSurveyData | null>(
    null,
  );
  const [teamSurveyLoading, setTeamSurveyLoading] = useState(false);
  const [teamSurveyError, setTeamSurveyError] = useState<string | null>(null);

  const { currentOrganization } = useOrganization();
  const { isCoach } = useProfile();
  const { defaultSurveyId } = useCoach();

  const questionnaireId = useMemo(() => {
    if (isCoach) {
      if (defaultSurveyId != null && defaultSurveyId > 0) {
        return defaultSurveyId;
      }
      return null;
    }
    const raw = currentOrganization?.default_survey;
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [isCoach, defaultSurveyId, currentOrganization?.default_survey]);

  const refetchTeamSurvey = useCallback(async () => {
    if (!selectedTeam?.id || !questionnaireId) {
      setTeamSurveyData(null);
      setTeamSurveyError(null);
      setTeamSurveyLoading(false);
      return;
    }

    setTeamSurveyLoading(true);
    setTeamSurveyError(null);
    try {
      const res = await getTeamSurvey({
        team_id: selectedTeam.id,
        questionnaire_id: questionnaireId,
      });
      if (res.success && res.data) {
        setTeamSurveyData(res.data);
        setTeamSurveyError(null);
      } else {
        setTeamSurveyData(null);
        setTeamSurveyError("Unable to load team survey data.");
      }
    } catch {
      setTeamSurveyData(null);
      setTeamSurveyError("Unable to load team survey data.");
    } finally {
      setTeamSurveyLoading(false);
    }
  }, [selectedTeam?.id, questionnaireId]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!selectedTeam?.id || !questionnaireId) {
        setTeamSurveyData(null);
        setTeamSurveyError(null);
        setTeamSurveyLoading(false);
        return;
      }

      setTeamSurveyLoading(true);
      setTeamSurveyError(null);
      try {
        const res = await getTeamSurvey({
          team_id: selectedTeam.id,
          questionnaire_id: questionnaireId,
        });
        if (cancelled) return;
        if (res.success && res.data) {
          setTeamSurveyData(res.data);
          setTeamSurveyError(null);
        } else {
          setTeamSurveyData(null);
          setTeamSurveyError("Unable to load team survey data.");
        }
      } catch {
        if (!cancelled) {
          setTeamSurveyData(null);
          setTeamSurveyError("Unable to load team survey data.");
        }
      } finally {
        if (!cancelled) {
          setTeamSurveyLoading(false);
        }
      }
    }

    void run();
    return () => {
      cancelled = true;
    };
  }, [selectedTeam?.id, questionnaireId]);

  const hasTeamCompletionProgress = useMemo(
    () => teamSurveyData != null && teamSurveyData.team_compl_percentage > 0,
    [teamSurveyData],
  );

  const value = useMemo<TeamContextValue>(
    () => ({
      selectedTeam,
      setSelectedTeam,
      selectedDepartment,
      setSelectedDepartment,
      teamSurveyData,
      teamSurveyLoading,
      teamSurveyError,
      hasTeamCompletionProgress,
      refetchTeamSurvey,
    }),
    [
      selectedTeam,
      selectedDepartment,
      teamSurveyData,
      teamSurveyLoading,
      teamSurveyError,
      hasTeamCompletionProgress,
      refetchTeamSurvey,
    ],
  );

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
};

export const useTeam = (): TeamContextValue => {
  const ctx = useContext(TeamContext);
  if (!ctx) {
    throw new Error("useTeam must be used within TeamProvider");
  }
  return ctx;
};
